import type {NodeParameters} from "@cm/graph-v3"
import {isNodeClass} from "@cm/graph-v3"
import {isInstanceOf} from "@cm/utils"
import {TemplateNode} from "@cm/template-nodes"
import type {TemplateNodeDefinition} from "@cm/template-nodes"
import type {Mattress} from "#mattress/nodes/mattress"

// String-identity check — `Mattress` transitively imports MattressComponentInstance → MattressCurveOutput →
// MattressOutput → MattressNode, so a value-import of `Mattress` here would create a load-time cycle.
const isMattress = isNodeClass<Mattress>("Mattress")

export function getParentMattressNode(node: unknown): Mattress {
    if (!(node instanceof TemplateNode)) throw new Error(`Expected template node, got ${typeof node}`)
    const traverse = (node: TemplateNode): Mattress | undefined => {
        if (isMattress(node)) return node
        const parents = Array.from(node.parents).filter(isInstanceOf(TemplateNode))
        for (const parent of parents) {
            const mattress = traverse(parent)
            if (mattress) return mattress
        }
        return undefined
    }

    const mattress = traverse(node)
    if (!mattress) throw new Error(`Failed to find parent mattress node for parameter node ${node.nodeClass}!`)
    return mattress
}

export abstract class MattressNode<
    out Parameters extends NodeParameters = NodeParameters,
    out ConstructorParameters extends NodeParameters = Parameters,
> extends TemplateNode<Parameters, ConstructorParameters> {
    public abstract override get definition(): TemplateNodeDefinition<Parameters, ConstructorParameters>

    override replaceParameters(newParameters: Parameters, disableValidation: true): void
    override replaceParameters(newParameters: ConstructorParameters, disableValidation?: false): void
    override replaceParameters(newParameters: ConstructorParameters | Parameters, disableValidation?: boolean): void {
        super.replaceParameters(newParameters as Parameters, disableValidation as true)
        const mattress = this.getParentMattressNodeSafe()
        if (mattress) mattress.updateParameters({})
    }

    getParentMattressNode(): Mattress {
        return getParentMattressNode(this)
    }

    getParentMattressNodeSafe(): Mattress | undefined {
        try {
            return this.getParentMattressNode()
        } catch (e) {
            return undefined
        }
    }
}
